/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: OverheadEntryDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * OverheadEntryDialog - Диалог создания/редактирования оверхеда с live-превью.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - FIX: пустой выбор ролей при создании; merge slotProps Autocomplete.
 * LAST_CHANGE: 2026-07-14 - UX: вертикальный скролл контента при нехватке высоты экрана.
 * LAST_CHANGE: 2026-07-14 - UX: заголовок, live-превью, секции формы, primary CTA, anti-autocomplete.
 * LAST_CHANGE: 2026-07-03 - UC-013: OverheadRecurrenceFields вместо periodUnit select.
 * END_CHANGE_SUMMARY
 */

import { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { APP_FORM_AUTOCOMPLETE, mergeAppDataFieldSlotProps } from '../../../utils/formFieldConfig';
import { brandColors } from '../../../theme/appTheme';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import {
  DEFAULT_INTERVAL_DAYS,
  DEFAULT_OVERHEAD_RECURRENCE_TYPE,
  OVERHEAD_RECURRENCE_TYPES,
} from '../../../models/overheadConstants';
import OverheadRolesSelect from './OverheadRolesSelect';
import OverheadRecurrenceFields from './OverheadRecurrenceFields';
import {
  buildOverheadPayload,
  formatOverheadPreviewLabel,
  isOverheadHoursValid,
  isRecurrenceValid,
} from './overheadUtils';

const displayFont = '"Syne", "Inter", sans-serif';
const overheadAccent = brandColors.muted;

/* START_METHOD_FormSection */
/* START_CONTRACT: FormSection
 * PURPOSE: Визуальная секция формы оверхеда с заголовком и описанием.
 * INPUTS: { title: string, description?: string, children: ReactNode }.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: FormSection
 */
function FormSection({ title, description, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: 'background.default',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: description ? 0.5 : 1.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {description}
        </Typography>
      )}
      {children}
    </Paper>
  );
}
/* END_METHOD_FormSection */

/* START_METHOD_OverheadEntryDialog */
/* START_CONTRACT: OverheadEntryDialog
 * PURPOSE: Диалог создания/редактирования накладного расхода проекта.
 * INPUTS: { open, onClose, onSubmit, overhead, roles }.
 * OUTPUTS: { JSX.Element - диалог с формой }.
 * SIDE_EFFECTS: вызов onSubmit (HTTP наверх); локальный state формы.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: OverheadEntryDialog
 */
export default function OverheadEntryDialog({
  open,
  onClose,
  onSubmit,
  overhead,
  roles,
}) {
  const firstFieldRef = useRef(null);
  const { dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const [name, setName] = useState('');
  const [hours, setHours] = useState('');
  const [recurrenceType, setRecurrenceType] = useState(DEFAULT_OVERHEAD_RECURRENCE_TYPE);
  const [intervalDays, setIntervalDays] = useState(String(DEFAULT_INTERVAL_DAYS));
  const [daysOfWeek, setDaysOfWeek] = useState([1, 2, 3, 4, 5]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(overhead);

  useEffect(() => {
    if (!open) return;

    if (overhead) {
      setName(overhead.name);
      setHours(String(overhead.hoursPerEmployee));
      setRecurrenceType(overhead.recurrenceType ?? DEFAULT_OVERHEAD_RECURRENCE_TYPE);
      setIntervalDays(
        overhead.intervalDays != null ? String(overhead.intervalDays) : String(DEFAULT_INTERVAL_DAYS),
      );
      setDaysOfWeek(overhead.daysOfWeek?.length ? [...overhead.daysOfWeek] : [1, 2, 3, 4, 5]);
      const selected = roles.filter((role) => overhead.roleIds.includes(role.id));
      setSelectedRoles(selected);
      return;
    }

    setName('');
    setHours('');
    setRecurrenceType(DEFAULT_OVERHEAD_RECURRENCE_TYPE);
    setIntervalDays(String(DEFAULT_INTERVAL_DAYS));
    setDaysOfWeek([1, 2, 3, 4, 5]);
    setSelectedRoles([]);
  }, [open, overhead, roles]);

  const hoursValid = isOverheadHoursValid(hours);
  const recurrenceValid = isRecurrenceValid({ recurrenceType, intervalDays, daysOfWeek });
  const canSave = name.trim().length > 0 && hoursValid && recurrenceValid
    && selectedRoles.length > 0 && !loading;

  const previewLabel = formatOverheadPreviewLabel({
    name,
    hours,
    recurrenceType,
    intervalDays,
    daysOfWeek,
    roles: selectedRoles,
  });

  const headerTitle = name.trim() || (isEdit ? overhead?.name : 'Новая запись');

  /* START_METHOD_handleSubmit */
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!canSave) return;
    setLoading(true);
    try {
      await onSubmit(buildOverheadPayload({
        name,
        hours,
        recurrenceType,
        intervalDays: recurrenceType === OVERHEAD_RECURRENCE_TYPES.INTERVAL ? intervalDays : null,
        daysOfWeek: recurrenceType === OVERHEAD_RECURRENCE_TYPES.WEEKLY ? daysOfWeek : null,
        roles: selectedRoles,
      }));
      /* START_BLOCK_SUBMIT_OK */
      onClose();
      /* END_BLOCK_SUBMIT_OK */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleSubmit */

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="overhead-entry-title"
      slotProps={{
        transition: dialogTransitionSlotProps,
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100% - 64px)',
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        autoComplete={APP_FORM_AUTOCOMPLETE}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundImage: `linear-gradient(180deg, ${alpha(overheadAccent, 0.12)} 0%, transparent 100%)`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.12em',
              lineHeight: 1.4,
              mb: 0.5,
            }}
          >
            Накладное время
          </Typography>
          <Typography
            id="overhead-entry-title"
            variant="h5"
            component="h2"
            noWrap
            sx={{
              fontFamily: displayFont,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {headerTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {isEdit
              ? 'Измените параметры записи — они учитываются при планировании нагрузки.'
              : 'Регулярные часы, которые команда тратит помимо задач: планёрки, ревью, синки.'}
          </Typography>
        </Box>

        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pt: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Paper
            variant="outlined"
            aria-hidden
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              pl: 2.5,
              pr: 2,
              py: 1.5,
              overflow: 'hidden',
              borderColor: alpha(overheadAccent, 0.25),
              bgcolor: alpha(overheadAccent, 0.05),
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                bgcolor: overheadAccent,
              }}
            />
            <ScheduleOutlinedIcon
              sx={{ mt: 0.25, color: overheadAccent, flexShrink: 0 }}
              fontSize="small"
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                Как запись появится в списке
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                {previewLabel}
              </Typography>
            </Box>
          </Paper>

          <FormSection
            title="Основное"
            description="Краткое название и часы на одного сотрудника за одно срабатывание правила."
          >
            <TextField
              inputRef={firstFieldRef}
              fullWidth
              label="Название"
              name="teamplanner-overhead-name"
              placeholder="Например, Дейли"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              required
              disabled={loading}
              slotProps={mergeAppDataFieldSlotProps({
                htmlInput: { maxLength: 200 },
              })}
            />
            <TextField
              fullWidth
              label="Часов на сотрудника"
              name="teamplanner-overhead-hours"
              type="number"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              size="small"
              required
              sx={{ mt: 2 }}
              error={hours !== '' && !hoursValid}
              helperText={
                hours !== '' && !hoursValid
                  ? 'Значение от 0.01 до 10 000 (например, 0.25 = 15 минут)'
                  : 'Дробные значения допустимы: 0.25 = 15 минут'
              }
              disabled={loading}
              slotProps={mergeAppDataFieldSlotProps({
                htmlInput: { min: 0.01, max: 10000, step: 'any' },
              })}
            />
          </FormSection>

          <FormSection
            title="Периодичность"
            description="Как часто эти часы добавляются к нагрузке каждого выбранного сотрудника."
          >
            <OverheadRecurrenceFields
              recurrenceType={recurrenceType}
              intervalDays={intervalDays}
              daysOfWeek={daysOfWeek}
              onRecurrenceTypeChange={setRecurrenceType}
              onIntervalDaysChange={setIntervalDays}
              onDaysOfWeekChange={setDaysOfWeek}
              disabled={loading}
            />
          </FormSection>

          <FormSection
            title="Роли"
            description="Оверхед применяется только к сотрудникам с выбранными ролями."
          >
            <OverheadRolesSelect
              label="Роли"
              roles={roles}
              value={selectedRoles}
              onChange={setSelectedRoles}
              disabled={loading}
            />
          </FormSection>
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Button size="small" onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            {isEdit ? 'Сохранить изменения' : 'Добавить запись'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
/* END_METHOD_OverheadEntryDialog */
